import { css, html, htmlArray } from "../helpers/webComponents";
import { BlackjackService } from "../services/BlackjackService";

/** CSS affecting the {@link BlackjackComponent} */
const styles: string = css`
    .content {
        display: flex;
        justify-content: space-between;
        align-items: flex-start; 
        position: relative; 
    }

    img {
        height: 140px;
        width: 100px;
    }

    .player-container, .dealer-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 500px;
    }

    .cards {
        display: flex;
        justify-content: center;
        flex-wrap: wrap;
    }

    .buttons {
        position: absolute;
        bottom: -200px;
        left: 50%;
        transform: translateX(-50%);
        display: flex;
        justify-content: center;
    }

    button {
        font-family: "Onesize";
        font-size: 18px;
        padding: 12px 25px;
        margin: 15px;
        cursor: pointer;
        border-radius: 8px;
        font-weight: 600;
        color: white;
        transition: all 0.3s ease;
        text-align: center;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    }

    .hit {
        border: 2px solid #28a745;
        background-color: #28a745;
    }

    .stand {
        border: 2px solid #dc3545;
        background-color: #dc3545;
    }

    .hit:hover {
        background-color: #218838;
    }

    .stand:hover {
        background-color: #c82333;
    }

    #result {
        margin-top: -150px;
    }
`;
/** Represents the BlackjackComponent */
export class BlackjackComponent extends HTMLElement {
    private readonly _blackjackService: BlackjackService = new BlackjackService();

    /**
     * Creates a new instance of the BlackjackComponent
     */
    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });

        this.render();
        this.startGame();
    }

    /**
     * Render the contents of this page
     */
    private render(): void {
        if (!this.shadowRoot) {
            return;
        }

        const elements: HTMLElement[] = htmlArray`
            <style>
                ${styles}
            </style>

            ${this.renderContent()}
        `;

        while (this.shadowRoot.firstChild) {
            this.shadowRoot.firstChild.remove();
        }

        this.shadowRoot.append(...elements);
    }

    /**
     * Render the content element
     */
    private renderContent(): HTMLElement {
        return html`
                <div class="content">
                    <div class="player-container">
                        <h2>Player: <span id="player-total"></span></h2>
                        <div class="cards" id="player-cards"></div>
                    </div>

                    <div class="dealer-container">
                        <h2>Dealer: <span id="dealer-total"></span></h2>
                        <div class="cards" id="dealer-cards">
                            <img id="hidden" src="/assets/img/rooms/Cards/back.png">
                        </div>
                    </div>

                    <div class="buttons">
                        <button class="hit">Hit</button>
                        <button class="stand">Stand</button>
                        <div id="result"></div>
                    </div>
                </div>
        `;
    }

    /**
     * Shows the dealers card
     */
    private getDealerCards(): void {
        const dealerDrawnCards: string[] = this._blackjackService.playDealerTurn();

        dealerDrawnCards.forEach((card: string) => {
            const img: HTMLImageElement = document.createElement("img");
            img.src = `/assets/img/rooms/Cards/${card}.png`;

            this.shadowRoot!.querySelector("#dealer-cards")?.append(img);
        });
    }

    /**
     * Shows the players card
     */
    private getPlayerCards(): void {
        const playerDrawnCards: string[] = this._blackjackService.playPlayerTurn();

        playerDrawnCards.forEach((card: string) => {
            const img: HTMLImageElement = document.createElement("img");
            img.src = `/assets/img/rooms/Cards/${card}.png`;

            this.shadowRoot!.querySelector("#player-cards")?.append(img);
        });
    }

    /**
     * Starts the game by giving cards to the dealer and player and sets up the buttons.
     */
    private startGame(): void {
        const hiddenImg: HTMLImageElement = this.shadowRoot!.querySelector("#hidden") as HTMLImageElement;
        const hiddenCard: string = this._blackjackService.dealHiddenCard();

        this.getDealerCards();
        this.getPlayerCards();
        this.addEventListeners(hiddenImg, hiddenCard);
    }

    private addEventListeners(hiddenImg: HTMLImageElement, hiddenCard: string): void {
        this.shadowRoot!.querySelector(".hit")?.addEventListener("click", () => this.hit());
        this.shadowRoot!.querySelector(".stand")?.addEventListener("click", () => this.stand(hiddenImg, hiddenCard));
    }

    /**
     * Adds a card to the player's hand when they choose to hit.
     * If the player's total exceeds 21 (busts), the hit button is disabled.
     */
    private hit(): void {
        this.showCardToPlayer();

        if (this._blackjackService.playerScore > 21) {
            const button: HTMLButtonElement = this.shadowRoot!.querySelector(".hit") as HTMLButtonElement;
            button.disabled = true;
        }
    }

    /**
     * Ends the player's turn, reveals the dealer's hidden card and determines the winner.
     */
    private async stand(hiddenCardImg: HTMLImageElement, hiddenCard: string): Promise<void> {
        hiddenCardImg.src = `/assets/img/rooms/Cards/${hiddenCard}.png`;
        const result: string = await this._blackjackService.result();
        this.showGameResult(result);
        console.log(this._blackjackService.playerDrawnCards);
        console.log(this._blackjackService.dealerDrawnCards);
    }

    /** Displays the game result, shows the scores and removes the buttons. */
    private showGameResult(result: string): void {
        this.shadowRoot!.querySelector(".hit")?.remove();
        this.shadowRoot!.querySelector(".stand")?.remove();

        const resultElement: HTMLElement | null = this.shadowRoot!.querySelector("#result");

        if (resultElement) {
            resultElement.innerHTML = result;
        }

        const playerTotal: HTMLElement | null = this.shadowRoot!.querySelector("#player-total");

        if (playerTotal) {
            playerTotal.innerText = String(this._blackjackService.playerScore);
        }

        const dealerTotal: HTMLElement | null = this.shadowRoot!.querySelector("#dealer-total");

        if (dealerTotal) {
            dealerTotal.innerText = String(this._blackjackService.dealerScore);
        }
    }

    /** Adds a card to the player's hand and updates their total score. */
    private showCardToPlayer(): void {
        const card: string = this._blackjackService.addCardToPlayer();

        const img: HTMLImageElement = document.createElement("img");
        img.src = `/assets/img/rooms/Cards/${card}.png`;

        this.shadowRoot!.querySelector("#player-cards")?.append(img);
    }
}
